import React, { useState } from 'react';
import {
  Modal,
  ModalFooter,
  ModalHeader,
  Box,
  Button,
  ModalBody,
  Alert,
} from '@razorpay/blade/components';
import { FBPAllocationTable } from './FBPAllocationsAndDeclarations/AllocateDeclareFBPTable';
import { useAssignedComponents } from '../hooks/useAssignedComponents';
import { MetaInfo } from '../types';
import { useAllocateMutations } from '../hooks/useAllocateMutations';
import { FBPAllocateDeclareHeader } from './FBPAllocationsAndDeclarations/FBPAllocateDeclareHeader';
import { ConfirmationModal } from './FBPAllocationsAndDeclarations/ConfirmationModal';

interface AllocateDeclareFBPModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string | number | undefined;
  isAdmin: boolean;
  userName: string;
}

export const FBPAllocationDeclarationModal = ({
  isOpen,
  onClose,
  userId,
  isAdmin,
  userName = '',
}: AllocateDeclareFBPModalProps) => {
  const {
    flexibleComponentsData,
    isFetching: isFetchingAssignedComponents,
    isError: hasError,
    isLoading,
    monthlyDeclarations,
    yearlyDeclarationsInAmount,
    onMonthlyDeclarationChange,
    monthlyDeclarationSnapShot,
    selectedIdSnapShot,
    selectedIds,
    onSelectedIdsChange,
    invalidateQueries,
    monthlyDeclarationErrors,
    setMonthlyDeclarationErrors,
    totalAllocated,
    totalAssigned,
    totalUnallocated,
    refetchData,
  } = useAssignedComponents({ userId, shouldGetUnassignedComponents: isAdmin });

  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const meta: MetaInfo = {
    userId,
    getValue: (id: string) => monthlyDeclarations[id],
    now: Math.floor(Date.now() / 1000).toString(),
  };

  const closeConfirmationModal = () => setShowConfirmationModal(false);

  const { handleAllocateClick, isRunning, buildOps, checkForErrors } = useAllocateMutations({
    selectedIds,
    monthlyDeclarations,
    previousSelectedIds: selectedIdSnapShot.current,
    previousMonthlyDeclarations: monthlyDeclarationSnapShot.current,
    meta,
    invalidateQueries,
    flexibleComponentsData,
    onSuccess: onClose,
    closeConfirmationModal,
    isAdmin,
  });
  const { createIds, removeIds, updatedAssignments } = buildOps();

  const isDisabled =
    (Object.keys(updatedAssignments).length === 0 &&
      (!isAdmin || (createIds.length === 0 && removeIds.length === 0))) ||
    isFetchingAssignedComponents;

  const handleSubmit: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    const errors = checkForErrors();
    if (Object.keys(errors).length) {
      setMonthlyDeclarationErrors(errors);
      return;
    }
    if (isAdmin) {
      setShowConfirmationModal(true);
      return;
    }
    handleAllocateClick();
  };

  const isFetching = isRunning;
  const showErrorAlert = (!isFetching || !isLoading) && hasError;
  const showLoader = isLoading || isFetchingAssignedComponents;

  return (
    <>
      <Modal isOpen={isOpen} onDismiss={onClose} size="full">
        <>
          <ModalHeader title="Allocate or declare FBP" subtitle="Learn more about FBP" />

          <ModalBody height="100%">
            {showErrorAlert ? (
              <Alert
                marginTop={'spacing.7'}
                description={
                  'We are facing some unknown error to fetch FBP data. Please try again.'
                }
                color="negative"
                isFullWidth
                isDismissible={false}
                actions={{
                  primary: {
                    text: 'Retry',
                    onClick: refetchData,
                  },
                }}
              />
            ) : (
              <Box maxWidth="886px" marginX="auto">
                <FBPAllocateDeclareHeader
                  isAdmin={isAdmin}
                  totalAllocated={totalAllocated}
                  yearlyDeclarationsInAmount={yearlyDeclarationsInAmount}
                />
                <FBPAllocationTable
                  monthlyDeclaration={monthlyDeclarations}
                  flexibleData={flexibleComponentsData}
                  onChange={onMonthlyDeclarationChange}
                  setSelectedRows={onSelectedIdsChange}
                  isFetching={isFetching}
                  isLoading={showLoader}
                  selectedRows={selectedIds}
                  monthlyDeclarationErrors={monthlyDeclarationErrors}
                  isAdmin={isAdmin}
                />
                {showConfirmationModal && (
                  <ConfirmationModal
                    isOpen={showConfirmationModal}
                    closeModal={closeConfirmationModal}
                    isDisabled={isRunning}
                    isLoading={isRunning}
                    primaryCTAAction={handleAllocateClick}
                    secondaryCTAAction={closeConfirmationModal}
                    userName={userName}
                  />
                )}
              </Box>
            )}
          </ModalBody>

          <ModalFooter>
            <Box display="flex" justifyContent="space-between" width="100%">
              <Button size="medium" variant="tertiary" onClick={onClose}>
                Close
              </Button>
              <Button
                size="medium"
                onClick={handleSubmit}
                isDisabled={isDisabled}
                isLoading={isRunning}>
                {isAdmin ? 'Allocate FBP' : 'Submit declaration'}
              </Button>
            </Box>
          </ModalFooter>
        </>
      </Modal>
    </>
  );
};
